import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatDate";
import {
  FileJson,
  ChevronLeft,
  ChevronRight,
  GitCompare,
  Eye,
  Trash2,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Clock,
  XCircle,
  Upload,
  X,
  File,
  FileText,
} from "lucide-react";
import { cobGetProcessWorkOrders, cobGetProcessStats, cobDeleteProcessWorkOrder, cobProcessV1 } from "../api";
import "./COBWorkOrdersList.css";

// Custom Dropdown Component for Status Filter
const CustomStatusDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const options = [
    { value: "all", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "processing", label: "Processing" },
    { value: "completed", label: "Completed" },
    { value: "failed", label: "Failed" },
  ];

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="cob-custom-dropdown" ref={dropdownRef}>
      <button className="cob-dropdown-trigger" onClick={() => setIsOpen(!isOpen)}>
        <span>{selectedOption?.label}</span>
        <svg className={`dropdown-arrow ${isOpen ? "open" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
      {isOpen && (
        <div className="cob-dropdown-menu">
          {options.map((option) => (
            <button
              key={option.value}
              className={`cob-dropdown-option ${value === option.value ? "selected" : ""}`}
              onClick={() => { onChange(option.value); setIsOpen(false); }}
            >
              {option.label}
              {value === option.value && <span className="check-mark">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Process Upload Modal Component
const ProcessUploadModal = ({ isOpen, onClose, onUploadComplete }) => {
  const [file, setFile] = useState(null);
  const [originalXml, setOriginalXml] = useState("");
  const [xmlFile, setXmlFile] = useState(null);
  const [uid, setUid] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const xmlFileInputRef = useRef(null);

  const modelConfig = {
    name: "COB v1 Process",
    description: "Google Gemini - Full Pipeline",
    color: "#7c3aed",
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      const validExts = ['.pdf', '.tiff', '.tif', '.jpg', '.jpeg', '.png'];
      const isValid = validExts.some(ext => selectedFile.name.toLowerCase().endsWith(ext));
      if (!isValid) {
        alert('Only PDF, TIFF, JPG, PNG files are supported.');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleXmlFileSelect = (event) => {
    const selectedXmlFile = event.target.files?.[0];
    if (selectedXmlFile) {
      setXmlFile(selectedXmlFile);
      // Read file content into originalXml state
      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalXml(e.target.result);
      };
      reader.readAsText(selectedXmlFile);
    }
  };

  const handleXmlTextChange = (e) => {
    setOriginalXml(e.target.value);
    // Clear file if user starts typing
    if (xmlFile && e.target.value !== originalXml) {
      setXmlFile(null);
    }
  };

  const clearXmlFile = () => {
    setXmlFile(null);
    setOriginalXml("");
  };

  const handleProcess = async () => {
    if (!file || !originalXml) return;
    
    setUploading(true);
    setError(null);

    try {
      const result = await cobProcessV1(file, originalXml, uid || undefined);
      setUploading(false);
      if (onUploadComplete) {
        onUploadComplete([{ filename: file.name, success: true, work_order_id: result.work_order?.id }]);
      }
      handleClose();
    } catch (err) {
      setUploading(false);
      setError(err.message || 'Process failed');
    }
  };

  const handleClose = () => {
    setFile(null);
    setOriginalXml("");
    setXmlFile(null);
    setUid("");
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="cob-modal-overlay" onClick={handleClose}>
      <div className="cob-upload-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div className="cob-modal-header" style={{ borderBottomColor: modelConfig.color }}>
          <h3>
            <GitCompare size={20} style={{ color: modelConfig.color }} />
            {modelConfig.name}
          </h3>
          <button className="cob-modal-close" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        <div className="cob-modal-body">
          {/* Document Upload */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>Document File *</label>
            <div
              className="cob-drop-zone"
              onClick={() => fileInputRef.current?.click()}
              style={{ minHeight: 80, borderColor: file ? modelConfig.color : undefined, cursor: 'pointer' }}
            >
              {file ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FileText size={24} style={{ color: modelConfig.color }} />
                  <div>
                    <p style={{ margin: 0, fontWeight: 500 }}>{file.name}</p>
                    <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <button 
                    onClick={(e) => { e.stopPropagation(); setFile(null); }}
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <>
                  <Upload size={32} style={{ color: modelConfig.color }} />
                  <p>Drop document or click to browse</p>
                  <span className="cob-drop-hint">PDF, TIFF, JPG, PNG</span>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.tiff,.tif,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          {/* Original XML - File or Text */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>Original XML * (for comparison)</label>
            
            {/* XML File Upload Zone */}
            <div
              className="cob-drop-zone"
              onClick={() => !xmlFile && xmlFileInputRef.current?.click()}
              style={{ 
                minHeight: 60, 
                borderColor: xmlFile ? '#16a34a' : undefined, 
                cursor: xmlFile ? 'default' : 'pointer',
                marginBottom: 8,
                background: xmlFile ? '#f0fdf4' : undefined
              }}
            >
              {xmlFile ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <File size={20} style={{ color: '#16a34a' }} />
                  <div>
                    <p style={{ margin: 0, fontWeight: 500 }}>{xmlFile.name}</p>
                    <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>{(xmlFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <button 
                    onClick={(e) => { e.stopPropagation(); clearXmlFile(); }}
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <>
                  <Upload size={24} style={{ color: '#64748b' }} />
                  <p style={{ margin: '4px 0 0', fontSize: 13 }}>Upload XML file or paste below</p>
                </>
              )}
              <input
                ref={xmlFileInputRef}
                type="file"
                accept=".xml"
                onChange={handleXmlFileSelect}
                style={{ display: 'none' }}
              />
            </div>

            {/* OR divider */}
            {!xmlFile && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 10, 
                margin: '8px 0',
                color: '#94a3b8',
                fontSize: 12
              }}>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }}></div>
                <span>OR paste XML content</span>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }}></div>
              </div>
            )}

            {/* Textarea for pasting */}
            {!xmlFile && (
              <textarea
                value={originalXml}
                onChange={handleXmlTextChange}
                placeholder="Paste original XML content here..."
                rows={5}
                style={{ 
                  width: '100%', 
                  fontFamily: 'monospace', 
                  fontSize: 11,
                  padding: 10,
                  borderRadius: 6,
                  border: '1px solid #e2e8f0',
                  resize: 'vertical'
                }}
              />
            )}

            {originalXml && !xmlFile && (
              <p style={{ fontSize: 12, color: '#16a34a', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                <CheckCircle size={14} /> {originalXml.length.toLocaleString()} characters
              </p>
            )}
          </div>

          {/* UID */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>UID (optional)</label>
            <input
              type="text"
              value={uid}
              onChange={(e) => setUid(e.target.value)}
              placeholder="Tracking ID"
              style={{ 
                width: '100%', 
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #e2e8f0'
              }}
            />
          </div>

          {/* Error */}
          {error && (
            <div style={{ 
              padding: 12, 
              background: '#fef2f2', 
              borderRadius: 6, 
              color: '#dc2626',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 16
            }}>
              <AlertCircle size={16} />
              {error}
            </div>
          )}
        </div>

        <div className="cob-modal-footer">
          <button className="cob-btn-cancel" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="cob-btn-upload-all"
            style={{ backgroundColor: modelConfig.color }}
            onClick={handleProcess}
            disabled={!file || !originalXml || uploading}
          >
            {uploading ? (
              <>
                <RefreshCw size={16} className="spin" />
                Processing...
              </>
            ) : (
              <>
                <GitCompare size={16} />
                Process
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const COBProcessWorkOrdersV1 = () => {
  const navigate = useNavigate();

  const [workOrders, setWorkOrders] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0, page: 1, pages: 1, limit: 10, skip: 0,
  });
  const [stats, setStats] = useState({
    total: 0, completed: 0, processing: 0, pending: 0, failed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  const searchTimerRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async (skip = 0, search = searchTerm) => {
    try {
      setLoading(true);
      setError("");

      const [ordersRes, statsRes] = await Promise.all([
        cobGetProcessWorkOrders({
          skip,
          limit: 10,
          status: statusFilter !== "all" ? statusFilter : undefined,
          version: "v1",
          search: search || undefined,
        }),
        cobGetProcessStats("v1"),
      ]);

      if (ordersRes.work_orders || ordersRes.data) {
        const orders = ordersRes.work_orders || ordersRes.data || [];
        setWorkOrders(orders);
        setPagination(ordersRes.pagination || { 
          total: orders.length, 
          page: Math.floor(skip / 10) + 1, 
          pages: Math.ceil((ordersRes.total || orders.length) / 10), 
          limit: 10, 
          skip 
        });
      }

      if (statsRes) {
        setStats({
          total: statsRes.total_work_orders || statsRes.total || 0,
          completed: statsRes.by_status?.completed || statsRes.completed || 0,
          processing: statsRes.by_status?.processing || statsRes.processing || 0,
          pending: statsRes.by_status?.pending || statsRes.pending || 0,
          failed: statsRes.by_status?.failed || statsRes.failed || 0,
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    
    searchTimerRef.current = setTimeout(() => {
      fetchData(0, value);
    }, 500);
  };

  const handleUploadComplete = (results) => {
    fetchData(0);
    const successResults = results.filter(r => r.success);
    if (successResults.length === 1 && successResults[0].work_order_id) {
      navigate(`/cob/process-work-order/${successResults[0].work_order_id}`);
    }
  };

  const handleDelete = async (workOrderId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this process work order?")) return;

    try {
      await cobDeleteProcessWorkOrder(workOrderId, 'v1');
      fetchData(pagination.skip);
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.pages) {
      fetchData(pagination.skip + pagination.limit);
    }
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      fetchData(Math.max(0, pagination.skip - pagination.limit));
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      completed: "status-completed",
      processing: "status-processing",
      pending: "status-pending",
      failed: "status-failed",
    };
    return classes[status] || "status-pending";
  };

  const getComparisonSummary = (wo) => {
    if (!wo.comparison) return '-';
    const mismatches = wo.comparison.mismatches?.length || 0;
    const origOnly = wo.comparison.original_only?.length || 0;
    const ocrOnly = wo.comparison.ocr_only?.length || 0;
    if (mismatches === 0 && origOnly === 0 && ocrOnly === 0) {
      return <CheckCircle size={16} style={{ color: '#16a34a' }} />;
    }
    return (
      <span style={{ color: mismatches > 0 ? '#dc2626' : '#d97706' }}>
        {mismatches}M / {origOnly}O / {ocrOnly}N
      </span>
    );
  };

  const getValidationSummary = (wo) => {
    if (!wo.validation) return '-';
    const failures = wo.validation.fail?.length || 0;
    if (failures === 0) {
      return <CheckCircle size={16} style={{ color: '#16a34a' }} />;
    }
    return (
      <span style={{ color: '#dc2626' }}>
        <XCircle size={14} style={{ marginRight: 2 }} />
        {failures} fail
      </span>
    );
  };

  return (
    <div className="cob-work-orders-list">
      {/* Header with Model Info */}
      <div className="cob-header-banner" style={{ borderLeftColor: "#7c3aed" }}>
        <div className="cob-header-info">
          <h2 style={{ color: "#7c3aed" }}>
            <GitCompare size={24} />
            COB Process V1 Work Orders
          </h2>
          <p>Google Gemini - Full Pipeline (OCR → Merge → Validate → Compare)</p>
        </div>
        <div className="cob-header-actions">
          <button
            className="cob-btn-upload"
            onClick={() => setShowUploadModal(true)}
            style={{ backgroundColor: "#7c3aed" }}
          >
            <GitCompare size={18} /> New Process
          </button>
        </div>
      </div>

      {error && (
        <div className="cob-error-banner">
          {error}
          <button onClick={() => setError("")}>×</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="cob-stats-grid">
        <div className="cob-stat-card-sm">
          <div className="cob-stat-value">{stats.total}</div>
          <div className="cob-stat-label">Total</div>
        </div>
        <div className="cob-stat-card-sm completed">
          <div className="cob-stat-value">{stats.completed}</div>
          <div className="cob-stat-label">Completed</div>
        </div>
        <div className="cob-stat-card-sm processing">
          <div className="cob-stat-value">{stats.processing}</div>
          <div className="cob-stat-label">Processing</div>
        </div>
        <div className="cob-stat-card-sm pending">
          <div className="cob-stat-value">{stats.pending}</div>
          <div className="cob-stat-label">Pending</div>
        </div>
        <div className="cob-stat-card-sm failed">
          <div className="cob-stat-value">{stats.failed}</div>
          <div className="cob-stat-label">Failed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="cob-filters-row">
        <input
          type="text"
          placeholder="Search by ID, filename, or UID..."
          className="cob-search-input"
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CustomStatusDropdown value={statusFilter} onChange={setStatusFilter} />
        <button className="cob-btn-refresh" onClick={() => fetchData(0)}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="cob-table-container">
        <table className="cob-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Filename</th>
              <th>Version</th>
              <th>Status</th>
              <th>Comparison</th>
              <th>Validation</th>
              <th>Created</th>
              <th>Duration</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="cob-table-loading">
                  <RefreshCw size={20} className="spin" /> Loading...
                </td>
              </tr>
            ) : workOrders.length === 0 ? (
              <tr>
                <td colSpan="9" className="cob-table-empty">
                  No process work orders found. Click "New Process" to start.
                </td>
              </tr>
            ) : (
              workOrders.map((wo) => (
                <tr key={wo._id} onClick={() => navigate(`/cob/process-work-order/${wo._id}`)}>
                  <td className="cob-id-cell">{wo._id?.slice(-8)}</td>
                  <td className="cob-filename-cell" title={wo.filename}>
                    {wo.filename?.substring(0, 30) || wo.uid || "-"}
                    {wo.filename?.length > 30 && "..."}
                  </td>
                  <td>
                    <span
                      className="cob-version-badge"
                      style={{
                        backgroundColor: "#f3e8ff",
                        color: "#7c3aed",
                      }}
                    >
                      v1 - Gemini
                    </span>
                  </td>
                  <td>
                    <span className={`cob-status-badge ${getStatusBadgeClass(wo.status)}`}>
                      {wo.status}
                    </span>
                  </td>
                  <td>{getComparisonSummary(wo)}</td>
                  <td>{getValidationSummary(wo)}</td>
                  <td>{formatDate(wo.created_at)}</td>
                  <td>{wo.processing_time ? `${wo.processing_time.toFixed(1)}s` : "-"}</td>
                  <td className="cob-actions-cell" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="cob-action-btn view"
                      title="View Details"
                      onClick={() => navigate(`/cob/process-work-order/${wo._id}`)}
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      className="cob-action-btn json"
                      title="Download JSON"
                      onClick={(e) => {
                        e.stopPropagation();
                        const data = {
                          work_order_id: wo._id,
                          filename: wo.filename,
                          comparison: wo.comparison,
                          validation: wo.validation,
                        };
                        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `process_${wo.filename?.replace(/\.[^/.]+$/, "") || wo._id}.json`;
                        a.click();
                      }}
                    >
                      <FileJson size={16} />
                    </button>
                    <button
                      className="cob-action-btn delete"
                      title="Delete"
                      onClick={(e) => handleDelete(wo._id, e)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="cob-pagination">
        <div className="cob-pagination-info">
          Showing {workOrders.length > 0 ? pagination.skip + 1 : 0} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
        </div>
        <div className="cob-pagination-controls">
          <button onClick={handlePrevPage} disabled={pagination.page <= 1}>
            <ChevronLeft size={16} /> Previous
          </button>
          <span className="cob-page-indicator">
            Page {pagination.page} of {pagination.pages || 1}
          </span>
          <button onClick={handleNextPage} disabled={pagination.page >= pagination.pages}>
            Next <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Upload Modal */}
      <ProcessUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={handleUploadComplete}
      />
    </div>
  );
};

export default COBProcessWorkOrdersV1;
